import React from 'react';
import { BreadcrumbsItem } from '~/libs/ui/atoms/Breadcrumbs';

import { useAvailabilityMetadata } from './adapters/useAvailabilityMetadata';
import { Header } from './components/Header';
import { AVAILABILITY_PAGE_WIDTH } from './constants';
import { formatMetadataDate, formatSharesYearQuarter } from './utils/formatMetadataDate';

const METODIKA_BREADCRUMBS: BreadcrumbsItem[] = [{ name: 'Dostupnosti', link: '/availability' }, { name: 'Metodika' }];

function formatUpdatedDate(isoDate: string | undefined, isLoading: boolean): string {
	if (isLoading) {
		return '…';
	}

	return isoDate ? formatMetadataDate(isoDate) : '–';
}

export function AvailabilityMetodikaPage() {
	const { metadata, isLoading } = useAvailabilityMetadata();
	const quarterLabel = metadata ? formatSharesYearQuarter(metadata.sharesYearQuarter) : null;

	return (
		<div className='w-full bg-gray-50'>
			<Header breadcrumbs={METODIKA_BREADCRUMBS} title='Metodika' />

			<div className={`${AVAILABILITY_PAGE_WIDTH} mx-auto px-[40px] py-10 font-display`}>
				<div className='max-w-3xl space-y-8'>
					<section className='space-y-4 text-gray-900'>
						<p className='leading-7'>
							Monitor Dostupnosti LP automaticky počítá dostupnost léčivých přípravků ve smyslu{' '}
							<strong>§ 39c odst. 2 písm. a) zákona č. 48/1997 Sb.</strong>, o veřejném zdravotním pojištění pro zvolený
							kvartál
							{quarterLabel ? (
								<>
									{' '}
									<strong>{quarterLabel}</strong>
								</>
							) : null}
							. Pro výpočet je použita metodika využívána Státní ústavem pro kontrolu léčiv ve správních řízeních.
						</p>
						<p className='leading-7'>
							Dostupnost léčivého přípravku je vypočítána vždy pro <strong>uzavřený kvartál</strong>
							{quarterLabel ? (
								<>
									{' '}
									<strong>{quarterLabel}</strong>
								</>
							) : null}{' '}
							ze tří příslušných hlášení DIS-13. Výpočet probíhá z celkového objemu doporučených denních dávek (
							<strong>DDD</strong>) v dané ATC skupině, která se dále může dělit na přípravky obsažené v referenční
							skupině (<strong>RS</strong>) a bez referenční skupiny.
						</p>
						<p className='leading-7'>
							V případě absence DDD je pro výpočet dostupnosti využito údajů o počtu obvyklých denních terapeutických
							dávek v balení (<strong>ODTD</strong>) ze Seznamu cen a úhrad LP (<strong>SCAU</strong>), případně
							informace o počtu balení. Všechny uvedené hodnoty jsou vždy dostupné v exportovaném xlsx souboru.
						</p>
						<p className='leading-7'>
							Do výpočtu jsou zahrnuty dodávky léčivých přípravků následujících typů dodavatelů:{' '}
							<strong>
								lékárna, lékař, osoba poskytující zdravotní péči, transfúzní služba, zdravotnické zařízení a hygienická
								stanice.
							</strong>
						</p>
					</section>

					<section className='bg-cBlue-50 border-l-4 border-cBlue-500 px-6 py-5 rounded-r space-y-2'>
						<p className='leading-7 text-gray-900'>
							Léčivý přípravek je dostupný, když jeho podíl v ATC skupině, případně kombinaci ATC a RS, dosáhne hodnoty{' '}
							<strong>alespoň 5 % z celkového počtu</strong> (novelou zákona č. 48/1997 Sb. došlo od 1. 1. 2026 k
							posunutí hranice posuzování dostupnosti ze 3 % na 5 %). Výpočet obsahuje matematické zaokrouhlení na dvě
							desetinná místa.
						</p>
						<p className='leading-7 text-gray-900'>
							Monitor Dostupnosti LP zobrazuje informaci o dohodnutých nejvyšších cenách (<strong>DNC</strong>) a
							dohodách o úhradě (<strong>DoÚ</strong>) uvedených v aktuálním SCAU.
						</p>
					</section>

					<section className='space-y-4'>
						<h3 className='text-lg font-bold text-gray-900 uppercase tracking-wide'>Zdroje</h3>
						<div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
							<div className='bg-white border border-gray-200 rounded p-5 space-y-1'>
								<p className='text-labelsmall text-gray-500 uppercase tracking-wide'>Zdroj</p>
								<p className='text-base font-bold text-gray-900'>DIS-13</p>
								<p className='text-sm text-gray-700 leading-5'>
									Souhrnné informace o dodávkách léčivých přípravků distributorům a provozovatelům oprávněným k výdeji v
									ČR a v zahraničí
								</p>
								<p className='text-labelsmall text-gray-500 pt-1'>
									Aktualizováno ke dni: {formatUpdatedDate(metadata?.dis13UpdatedAt, isLoading)}
								</p>
							</div>
							<div className='bg-white border border-gray-200 rounded p-5 space-y-1'>
								<p className='text-labelsmall text-gray-500 uppercase tracking-wide'>Zdroj</p>
								<p className='text-base font-bold text-gray-900'>SCAU</p>
								<p className='text-sm text-gray-700 leading-5'>
									Seznam léčiv a PZLÚ hrazených ze zdravotního pojištění
								</p>
								<p className='text-labelsmall text-gray-500 pt-1'>
									Aktualizováno ke dni: {formatUpdatedDate(metadata?.scauUpdatedAt, isLoading)}
								</p>
							</div>
						</div>
					</section>

					<section className='border-t border-gray-200 pt-4'>
						<p className='text-gray-700 leading-7'>
							Máte dotazy k metodice? Kontaktujte nás na{' '}
							<a
								href='mailto:support@cogvio.com'
								className='text-cBlue-500 hover:text-blue-700 underline underline-offset-2 decoration-2'
							>
								support@cogvio.com
							</a>
						</p>
					</section>
				</div>
			</div>
		</div>
	);
}
